import { useEffect, useRef, useState, useCallback, useMemo, type ReactNode } from "react";
import type { CarCardData } from "@/lib/api";
import { buildCarPath } from "@/lib/api";
import { getCurrencySymbol } from "@workspace/api-client-react";
import { BRAND_GREEN } from "@/lib/design-tokens";
import { Sentry } from "@/lib/sentry";
import { isFiniteCoord, isValidCoord } from "@/lib/coordinate-guard";
import { acquirePublicMapLauncherSpacing } from "@/lib/intercom";

// Diagnostic telemetry for "blank map" reports we can't reproduce.
// We add a Sentry breadcrumb at each step in the init chain (script
// load, token fetch, mapkit.init, first region-change-end, first
// tile paint) and `captureMessage` on the failure paths the user
// would otherwise see as a silent blank box. No PII; the only
// dynamic data is timing (ms) and HTTP status — safe to ship.
function mapBreadcrumb(message: string, data?: Record<string, unknown>) {
  try {
    Sentry.addBreadcrumb({
      category: "mapkit",
      level: "info",
      message,
      data,
    });
  } catch {
    /* never let telemetry break the map */
  }
}

function mapCaptureMessage(
  message: string,
  level: "warning" | "error",
  extra?: Record<string, unknown>,
) {
  try {
    Sentry.captureMessage(message, {
      level,
      tags: { feature: "mapkit" },
      extra,
    });
  } catch {
    /* never let telemetry break the map */
  }
}

declare global {
  interface Window {
    mapkit: typeof mapkit;
  }
}

declare namespace mapkit {
  class Map {
    constructor(element: HTMLElement, options?: MapConstructorOptions);
    showItems(items: Annotation[], options?: { animate: boolean; padding: Padding }): void;
    addAnnotations(annotations: Annotation[]): void;
    removeAnnotations(annotations: Annotation[]): void;
    addOverlay(overlay: unknown): void;
    removeOverlay(overlay: unknown): void;
    overlays: unknown[];
    annotations: Annotation[];
    region: CoordinateRegion;
    destroy(): void;
    addEventListener(type: string, listener: (event: unknown) => void): void;
    removeEventListener(type: string, listener: (event: unknown) => void): void;
    center: Coordinate;
    cameraZoomRange: CameraZoomRange;
    showsCompass: number;
    showsZoomControl: boolean;
    showsMapTypeControl: boolean;
    isScrollEnabled: boolean;
    isZoomEnabled: boolean;
  }

  class Coordinate {
    constructor(latitude: number, longitude: number);
    latitude: number;
    longitude: number;
  }

  class CoordinateSpan {
    constructor(latitudeDelta: number, longitudeDelta: number);
  }

  class CoordinateRegion {
    constructor(center: Coordinate, span: CoordinateSpan);
    toBoundingRegion(): BoundingRegion;
  }

  class BoundingRegion {
    northLatitude: number;
    southLatitude: number;
    eastLongitude: number;
    westLongitude: number;
  }

  class CameraZoomRange {
    constructor(options: { minCameraDistance?: number; maxCameraDistance?: number });
  }

  class Padding {
    constructor(top: number, right: number, bottom: number, left: number);
  }

  class Annotation {
    constructor(coordinate: Coordinate, factory: (coordinate: Coordinate, options: Record<string, unknown>) => Element, options?: AnnotationOptions);
    coordinate: Coordinate;
    data: Record<string, unknown>;
    element: Element;
    clusteringIdentifier: string | null;
    displayPriority: number;
  }

  interface MapConstructorOptions {
    center?: Coordinate;
    region?: CoordinateRegion;
    showsCompass?: number;
    showsZoomControl?: boolean;
    showsMapTypeControl?: boolean;
    isScrollEnabled?: boolean;
    isZoomEnabled?: boolean;
  }

  interface AnnotationOptions {
    clusteringIdentifier?: string;
    displayPriority?: number;
    data?: Record<string, unknown>;
    animates?: boolean;
    accessibilityLabel?: string;
  }

  function init(options: { authorizationCallback: (done: (token: string) => void) => void }): void;

  const FeatureVisibility: { Hidden: number; Visible: number; Adaptive: number };

  class Search {
    constructor(options?: { region?: CoordinateRegion });
    search(query: string, callback: (error: Error | null, data: { places: Place[] }) => void): void;
  }

  interface Place {
    coordinate: Coordinate;
    name: string;
  }

  class Style {
    constructor(options: {
      strokeColor?: string;
      strokeOpacity?: number;
      lineWidth?: number;
      fillColor?: string;
      fillOpacity?: number;
    });
  }

  class CircleOverlay {
    constructor(coordinate: Coordinate, radius: number);
    style: Style;
  }
}

const MAPKIT_JS_URL = "https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js";

// How long after a programmatic camera move (map construction or a
// showItems() re-fit) we ignore region-change-end events, so those
// programmatic moves are never mistaken for a genuine user pan/zoom.
const PROGRAMMATIC_GUARD_MS = 800;

// Minimum number of Apple-MapKit *tile* fetches that prove a viewport
// actually painted. A correctly-rendered map fetches a GRID of tiles
// (the DRIVESHARE_V2-5F false-positive event had 60+); even the smallest
// visible viewport pulls several vtile/ttile requests. A genuinely-blank
// map produces ~zero (ad-blocker / proxy block) or only a couple before
// aborting (which is separately surfaced by the error-level mapkit
// captures). 4 sits safely above "blocked" and well below any real paint.
const APPLE_MAP_TILE_PAINT_FLOOR = 4;

// Matches an Apple-MapKit *viewport tile* fetch. A correctly-rendered map
// pulls a GRID of these; icons/shields/POI overlays and the mapkit.js script
// are deliberately excluded so a blank viewport that still loaded a couple of
// chrome assets isn't mistaken for paint. Exported for unit testing.
export const APPLE_MAP_TILE_URL_RE =
  /^https:\/\/cdn\.apple-mapkit\.com\/(?:ti\/tile|md\/v1\/(?:vtile|ttile))/;

export function isAppleMapTileEntry(name: string): boolean {
  return APPLE_MAP_TILE_URL_RE.test(name);
}

// True when MapKit has fetched enough tiles since `sinceMs` to prove the
// map painted. Used to suppress the blank-map safety-timer false positive
// (DRIVESHARE_V2-5F): a single static pin frames itself to the SAME region
// it was constructed with, so MapKit never fires `region-change-end` (a
// "camera moved" signal, not a "tiles painted" one) even though the map
// rendered fine. Cross-origin resource-timing hides HTTP status and zeroes
// sizes, so we can't read per-tile success directly — but the *count* of
// tile requests is a robust, status-agnostic discriminator: a blocked map
// never produces a full grid. Exported for unit testing.
//
// NOTE: `getEntriesByType("resource")` only sees what's still in the resource-
// timing buffer (default cap ~250). On a heavy authoring page (e.g.
// /dashboard/listings/:id/edit, the dominant DRIVESHARE_V2-5F culprit) the
// buffer overflows and EVICTS the tile entries, so this one-shot query can
// return a false negative even on a map that painted fine. The live init
// effect therefore prefers a `PerformanceObserver` counter (buffer-overflow
// immune — see installTilePaintCounter); this remains as a fallback for
// browsers without PerformanceObserver and for unit testing.
export function appleMapTilesPainted(sinceMs: number): boolean {
  if (typeof performance === "undefined" || !performance.getEntriesByType) {
    return false;
  }
  const tileEntries = performance.getEntriesByType("resource").filter(
    (e) => e.startTime >= sinceMs && isAppleMapTileEntry(e.name),
  );
  return tileEntries.length >= APPLE_MAP_TILE_PAINT_FLOOR;
}

// A live tile-paint counter that survives resource-timing-buffer overflow:
// a PerformanceObserver fires for every new `resource` entry AS IT'S RECORDED,
// regardless of whether the (capped) buffer is full, so it never loses tile
// fetches the way the one-shot `appleMapTilesPainted` query can. `buffered:
// true` also replays any tiles already recorded between map construction and
// observer install. Returns a handle exposing the running count + a disconnect
// for cleanup; `null` when PerformanceObserver is unavailable (caller falls
// back to appleMapTilesPainted). Exported for unit testing.
export interface TilePaintCounter {
  painted(): boolean;
  count(): number;
  disconnect(): void;
}

export function installTilePaintCounter(sinceMs: number): TilePaintCounter | null {
  if (typeof PerformanceObserver === "undefined") return null;
  let tiles = 0;
  let observer: PerformanceObserver | null = null;
  try {
    observer = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        if (e.startTime >= sinceMs && isAppleMapTileEntry(e.name)) tiles++;
      }
    });
    observer.observe({ type: "resource", buffered: true });
  } catch {
    return null;
  }
  return {
    painted: () => tiles >= APPLE_MAP_TILE_PAINT_FLOOR,
    count: () => tiles,
    disconnect: () => {
      try { observer?.disconnect(); } catch { /* noop */ }
    },
  };
}

// Classifies a token-fetch failure as a transient/non-actionable network blip
// rather than a real, persistent failure. The dominant `token-fetch-threw`
// noise (DRIVESHARE_V2-5R) is Safari's "Load failed" / an AbortError — i.e.
// the user navigated away mid-fetch or hit a momentary network drop, which a
// retry cannot fix and which is not an app defect. These must stay breadcrumbs
// even after retries are exhausted. Only ever called for errors that carry NO
// HTTP status (a real network rejection), so it never collides with the
// synthetic "Failed to fetch MapKit token (status N)" non-ok error.
export function isTransientFetchError(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name || "";
  if (name === "AbortError") return true;
  const msg = ((err as { message?: string } | null)?.message || String(err)).toLowerCase();
  return (
    msg.includes("load failed") || // Safari
    msg.includes("failed to fetch") || // Chrome
    msg.includes("networkerror") || // Firefox
    msg.includes("network request failed") ||
    msg.includes("network connection was lost") ||
    msg.includes("the operation was aborted") ||
    msg.includes("aborted") ||
    msg.includes("cancelled") ||
    msg.includes("canceled")
  );
}

// Stable per-car marker layout, computed once whenever the cars set
// changes. Holding both the true coordinate and the de-overlap spiral
// offset lets a selection-only change swap a single marker (price-pill
// ↔ car-photo) without recomputing — and without shifting any OTHER pin.
interface MarkerLayout {
  trueLat: number;
  trueLng: number;
  offLat: number;
  offLng: number;
  price: string;
  photoUrl: string | null;
  carPath: string;
  accessibilityLabel: string;
}

let mapkitLoadPromise: Promise<void> | null = null;
let mapkitInitialized = false;
let cachedToken: string | null = null;
let tokenFetchPromise: Promise<string> | null = null;
let mapkitPreconnectAdded = false;

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

async function fetchMapKitToken(): Promise<string> {
  if (cachedToken) return cachedToken;
  if (tokenFetchPromise) return tokenFetchPromise;

  const startedAt = performance.now();
  mapBreadcrumb("token-fetch:start");

  // The server mints the token locally and can intermittently 500 on a
  // transient re-mint hiccup that it recovers from on the very next request
  // (it now also serves a still-valid cached token through such a hiccup). A
  // lone failed attempt that then succeeds is recoverable noise, not an
  // actionable error — so retry once before reporting. Only when BOTH attempts
  // fail do we capture the (unchanged) `token-fetch-non-ok` / `token-fetch-threw`
  // error so a genuinely-persistent outage still pages, but a transient blip
  // no longer spikes Sentry.
  const MAX_ATTEMPTS = 2;
  const RETRY_DELAY_MS = 400;

  tokenFetchPromise = (async () => {
    let lastStatus: number | undefined;
    let lastError: unknown;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      // Reset per-attempt so `lastStatus` reflects ONLY the final attempt's
      // outcome: set when that attempt got an HTTP response (non-ok),
      // undefined when it was a network-level throw. This lets the
      // post-retry classifier distinguish a real server fault from a
      // transient network blip / navigation abort.
      lastStatus = undefined;
      try {
        const res = await fetch(`${API_BASE}/mapkit/token`);
        if (!res.ok) {
          lastStatus = res.status;
          throw new Error(`Failed to fetch MapKit token (status ${res.status})`);
        }
        const data = await res.json();
        cachedToken = data.token;
        mapBreadcrumb("token-fetch:ok", {
          status: res.status,
          attempt,
          durationMs: Math.round(performance.now() - startedAt),
        });
        mapBreadcrumb("token-fetch:parsed", {
          hasToken: typeof data.token === "string" && data.token.length > 0,
        });
        setTimeout(() => { cachedToken = null; tokenFetchPromise = null; }, 25 * 60 * 1000);
        return data.token as string;
      } catch (err) {
        lastError = err;
        mapBreadcrumb("token-fetch:attempt-failed", {
          attempt,
          status: lastStatus,
          error: (err as Error)?.message || String(err),
          durationMs: Math.round(performance.now() - startedAt),
        });
        if (attempt < MAX_ATTEMPTS) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
        }
      }
    }

    // Both attempts failed. Classify before escalating so the dashboard only
    // shows REAL, actionable failures and the dominant noise stays breadcrumbs:
    //   - HTTP 5xx        → genuine server fault → capture (token-fetch-non-ok).
    //   - HTTP 4xx        → deliberate rejection (edge bot-gate 403 = the
    //                       DRIVESHARE_V2-68 noise, rate-limit 429, gate 401):
    //                       retry can't help and it's not an app defect → breadcrumb.
    //   - network throw   → if transient (Safari "Load failed" / AbortError =
    //                       the DRIVESHARE_V2-5R noise: user navigated away or a
    //                       momentary drop) → breadcrumb; otherwise an unexpected
    //                       throw → capture (token-fetch-threw).
    // `attempts` records that we retried. Signal names are preserved for
    // existing Sentry grouping/alerts.
    tokenFetchPromise = null;
    const baseExtra = {
      attempts: MAX_ATTEMPTS,
      durationMs: Math.round(performance.now() - startedAt),
    };
    if (lastStatus !== undefined) {
      // Task #5896 — 502/503/504 are transient availability faults (the server
      // now returns 503 for a transient mint hiccup; a cold-boot / redeploy
      // rebind briefly emits 502/503/504 at the edge). Both attempts already
      // failed, but these are recoverable and self-heal on the next cycle, so
      // they must NOT escalate to Sentry — only a genuine 500 (or other 5xx)
      // is an actionable server fault worth capturing.
      const transientStatus =
        lastStatus === 502 || lastStatus === 503 || lastStatus === 504;
      if (lastStatus >= 500 && !transientStatus) {
        mapCaptureMessage("mapkit:token-fetch-non-ok", "error", {
          status: lastStatus,
          ...baseExtra,
        });
      } else if (transientStatus) {
        mapBreadcrumb("token-fetch:transient-5xx", {
          status: lastStatus,
          ...baseExtra,
        });
      } else {
        mapBreadcrumb("token-fetch:rejected-4xx", {
          status: lastStatus,
          ...baseExtra,
        });
      }
    } else if (isTransientFetchError(lastError)) {
      mapBreadcrumb("token-fetch:transient-after-retries", {
        error: (lastError as Error)?.message || String(lastError),
        ...baseExtra,
      });
    } else {
      mapCaptureMessage("mapkit:token-fetch-threw", "error", {
        error: (lastError as Error)?.message || String(lastError),
        ...baseExtra,
      });
    }
    throw lastError instanceof Error
      ? lastError
      : new Error("Failed to fetch MapKit token");
  })();

  return tokenFetchPromise;
}

function loadMapKitJS(): Promise<void> {
  if (mapkitLoadPromise) return mapkitLoadPromise;

  mapkitLoadPromise = new Promise((resolve, reject) => {
    if (window.mapkit) {
      mapBreadcrumb("script-load:already-present");
      resolve();
      return;
    }
    const startedAt = performance.now();
    mapBreadcrumb("script-load:start", { src: MAPKIT_JS_URL });
    const script = document.createElement("script");
    script.src = MAPKIT_JS_URL;
    script.crossOrigin = "anonymous";
    script.onload = () => {
      mapBreadcrumb("script-load:ok", {
        durationMs: Math.round(performance.now() - startedAt),
        hasGlobal: typeof window.mapkit !== "undefined",
      });
      // The most likely Chrome failure mode: ad-blocker / privacy
      // extension lets the request "succeed" with a stub response, so
      // onload fires but `window.mapkit` is undefined. Capture so we
      // can tell it apart from a real network failure.
      if (typeof window.mapkit === "undefined") {
        mapCaptureMessage("mapkit:script-loaded-but-no-global", "error", {
          src: MAPKIT_JS_URL,
          durationMs: Math.round(performance.now() - startedAt),
        });
      }
      resolve();
    };
    script.onerror = () => {
      // Task #5064 — the external Apple CDN failing to serve MapKit JS
      // (Sentry DRIVESHARE_V2-7P) is pure third-party-availability noise
      // (≈3s onerror, intermittent — ad-blockers, flaky network): the UI
      // degrades gracefully when MapKit is unavailable, so this is NOT an
      // actionable app signal. Task #4690 downgraded it from error to
      // warning, but a warning still creates a recurring Sentry event, so
      // record it as a breadcrumb only (silent unless a later, genuine
      // error fires and attaches this trail). The genuinely actionable
      // signals (token-fetch-*, script-loaded-but-no-global,
      // global-missing-after-load, auth-callback-failed) still capture.
      mapBreadcrumb("script-load:failed", {
        src: MAPKIT_JS_URL,
        durationMs: Math.round(performance.now() - startedAt),
      });
      reject(new Error("Failed to load MapKit JS"));
    };
    document.head.appendChild(script);
  });

  return mapkitLoadPromise;
}

let ensureMapKitPromise: Promise<void> | null = null;

function preconnectMapKitCDN(): void {
  if (mapkitPreconnectAdded || typeof document === "undefined") return;
  mapkitPreconnectAdded = true;
  try {
    const link = document.createElement("link");
    link.rel = "preconnect";
    link.href = "https://cdn.apple-mapkit.com";
    link.crossOrigin = "anonymous";
    document.head.appendChild(link);
  } catch {
    // A resource hint is an optimization only; MapKit still loads without it.
  }
}

// Best-effort, fire-and-forget prewarm of the slow MapKit network paths
// (cold DNS/TLS to Apple's CDN, the mapkit.js script download, and the
// `/api/mapkit/token` fetch) WITHOUT constructing a map. Call this early on
// a page that will show an AppleMap below the fold (e.g. the car detail
// "Approximate location" map) so that by the time the user scrolls the map
// into view, only the cheap tile paint remains — the expensive handshake +
// script + token are already resolved (or in-flight).
//
// This reuses ensureMapKit()'s module-level singletons (`mapkitLoadPromise`,
// `cachedToken`, `tokenFetchPromise`, `ensureMapKitPromise`), so it never
// duplicates work: the visibility-gated construction effect later awaits the
// SAME ensureMapKit() promise and consumes the already-resolved script/token.
// It intentionally does NOT call `new mapkit.Map(...)` — construction stays
// deferred behind waitForVisibleAndSized (load-bearing for iOS paint
// correctness). Failures are swallowed: a prewarm error must never surface to
// the user before the map is even shown; the real init chain reports it later.
export function prewarmMapKit(): void {
  try {
    // Add the connection hint only on a route that actually renders a map.
    // Keeping this out of the shared HTML shell avoids spending a connection
    // slot on every page while still removing the DNS/TLS cost for map routes.
    preconnectMapKitCDN();
    ensureMapKit().catch(() => {
      /* best-effort: the visibility-gated init chain reports real failures */
    });
  } catch {
    /* never let prewarm throw synchronously into the caller */
  }
}

export async function ensureMapKit(): Promise<void> {
  if (mapkitInitialized) return;
  if (ensureMapKitPromise) return ensureMapKitPromise;

  ensureMapKitPromise = (async () => {
    // The SDK and our short-lived authorization token come from independent
    // origins. Starting them together removes one full network wait from the
    // first map render; initialization still waits for both to be ready.
    try {
      await Promise.all([
        loadMapKitJS(),
        fetchMapKitToken(),
      ]);
    } catch (err) {
      // Token failures are retryable on a later mount. The underlying script
      // and token loaders retain their own successful/retryable state.
      ensureMapKitPromise = null;
      throw err;
    }
    if (mapkitInitialized) return;

    if (typeof window.mapkit === "undefined") {
      // Defence in depth: loadMapKitJS already captured this case, but
      // re-check here so the throw path below produces a useful Sentry
      // event with the full breadcrumb chain attached.
      ensureMapKitPromise = null;
      mapCaptureMessage("mapkit:global-missing-after-load", "error");
      throw new Error("MapKit JS loaded but window.mapkit is undefined");
    }

    mapBreadcrumb("mapkit-init:start");
    window.mapkit.init({
      authorizationCallback: async (done) => {
        try {
          // MapKit can request authorization again after the first token
          // expires, so resolve through the cache rather than closing over
          // the token used during initial prewarm.
          const token = await fetchMapKitToken();
          done(token);
        } catch (err) {
          mapCaptureMessage("mapkit:auth-callback-failed", "error", {
            error: (err as Error)?.message || String(err),
          });
          console.error("MapKit auth failed:", err);
          done("");
        }
      },
    });
    mapkitInitialized = true;
    mapBreadcrumb("mapkit-init:done");
  })();

  return ensureMapKitPromise;
}

/**
 * Task #1677 — one-shot reverse geocode: given (lat, lng), resolve a
 * human address ("10 Downing St, London") via MapKit's Geocoder so callers
 * (the trip detail page) can show a real address for delivery trips that
 * only have pickup coordinates on file.
 */
export async function reverseGeocodeCoords(
  lat: number,
  lng: number,
): Promise<string | null> {
  // Guard: a NaN or non-finite coord fed to `new mk.Coordinate(lat, lng)`
  // causes MapKit to throw "Expected a number for x in MapRect constructor but
  // got NaN" deep in its renderer.  Return early so the caller gets the same
  // `null` fallback it would get on a geocoder miss.
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  try {
    await ensureMapKit();
    const mk = window.mapkit;
    return new Promise((resolve) => {
      try {
        const geocoder = new (mk as any).Geocoder();
        geocoder.reverseLookup(
          new mk.Coordinate(lat, lng),
          (err: any, data: any) => {
            if (err || !data?.results?.[0]) {
              resolve(null);
              return;
            }
            const r = data.results[0];
            const parts = [r.name, r.locality, r.administrativeArea].filter(Boolean);
            resolve(parts.length > 0 ? parts.join(", ") : null);
          },
        );
      } catch {
        resolve(null);
      }
    });
  } catch {
    return null;
  }
}

export async function geocodeLocation(query: string): Promise<{ latitude: number; longitude: number } | null> {
  try {
    await ensureMapKit();
    const mk = window.mapkit;
    return new Promise((resolve) => {
      const search = new mk.Search();
      search.search(query, (error, data) => {
        if (error || !data.places || data.places.length === 0) {
          resolve(null);
          return;
        }
        const place = data.places[0];
        resolve({ latitude: place.coordinate.latitude, longitude: place.coordinate.longitude });
      });
    });
  } catch {
    return null;
  }
}

/**
 * Build a circular car-photo map marker element used site-wide.
 *
 * Renders a circular photo with a white border and soft shadow. If no photo URL
 * is provided, falls back to a small coloured dot so callers can use the same
 * helper everywhere a single car is pinpointed on a map.
 */
export function createCarPhotoMarkerElement(
  photoUrl?: string | null,
  options: { size?: number; fallbackColor?: string; anchorCenter?: boolean } = {},
): HTMLElement {
  const size = options.size ?? 64;
  const fallbackColor = options.fallbackColor ?? BRAND_GREEN;
  const el = document.createElement("div");
  // MapKit's default annotation anchor is the element's bottom-centre — that
  // makes a teardrop pin sit visually correct, but for a circular car-photo
  // marker we want the *centre* of the circle at the coordinate (otherwise
  // the marker drifts upward of the centred delivery-radius overlays). Push
  // the element down by 50% of its own height so its visual centre aligns
  // with MapKit's bottom-centre anchor.
  //
  // Callers that wrap the marker with a label underneath (e.g. trip pin with
  // a "PICK-UP / DROP-OFF" pill) must pass `anchorCenter: false` and apply
  // their own offset on the wrapper, otherwise this transform pushes the
  // circle down on top of the label.
  const anchorCenter = options.anchorCenter !== false;
  const ANCHOR_CENTER = anchorCenter ? "transform: translate(0, 50%);" : "";
  if (photoUrl) {
    el.style.cssText = `
      ${ANCHOR_CENTER}
      width: ${size}px; height: ${size}px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 12px rgba(0,0,0,0.25);
      overflow: hidden;
      background: #f0f5f0;
    `;
    const img = document.createElement("img");
    img.src = photoUrl;
    img.alt = "";
    img.style.cssText = `width: 100%; height: 100%; object-fit: cover;`;
    el.appendChild(img);
  } else {
    el.style.cssText = `
      ${ANCHOR_CENTER}
      width: 24px; height: 24px;
      background: ${fallbackColor}; border-radius: 50%;
      border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);
    `;
  }
  return el;
}

function createPricePillElement(price: string, selected = false): HTMLElement {
  const el = document.createElement("div");
  el.style.cssText = `
    background: ${selected ? "#1f2937" : "white"};
    color: ${selected ? "white" : "#111"};
    font-size: 13px;
    font-weight: 700;
    padding: 6px 12px;
    border-radius: 999px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.22);
    white-space: nowrap;
    cursor: pointer;
    border: 2px solid ${selected ? "#1f2937" : "white"};
    transition: transform 0.15s ease;
    line-height: 1;
  `;
  el.dataset.mapPricePill = selected ? "selected" : "default";
  el.textContent = price;
  el.addEventListener("mouseenter", () => { el.style.transform = "scale(1.1)"; });
  el.addEventListener("mouseleave", () => { el.style.transform = "scale(1)"; });
  return el;
}

function createSelectedCarMarkerElement(photoUrl: string | null, price: string): HTMLElement {
  const marker = document.createElement("div");
  marker.style.cssText = `
    position: relative;
    width: 56px;
    height: 56px;
    transform: translate(0, 50%);
  `;

  // Preserve the established photo marker and place the selected price pill
  // beneath it. The wrapper owns the centre anchor so the photo stays within
  // its 56px box and cannot overlap the pill.
  marker.appendChild(
    createCarPhotoMarkerElement(photoUrl, { size: 56, anchorCenter: false }),
  );

  const pillContainer = document.createElement("div");
  pillContainer.style.cssText = `
    position: absolute;
    top: 60px;
    left: 50%;
    transform: translateX(-50%);
  `;
  pillContainer.appendChild(createPricePillElement(price, true));
  marker.appendChild(pillContainer);

  return marker;
}

export interface AppleMapProps {
  cars?: CarCardData[];
  onBoundsChange?: (bounds: { north: number; south: number; east: number; west: number }) => void;
  onMarkerClick?: (slug: string, carId?: number) => void;
  latitude?: number;
  longitude?: number;
  height?: string;
  interactive?: boolean;
  /**
   * Controls map zoom independently from panning/scrolling. Defaults to the
   * value of `interactive` so existing callers keep their current behavior.
   */
  zoomEnabled?: boolean;
  className?: string;
  showSinglePin?: boolean;
  pinImageUrl?: string;
  /**
   * When provided, the matching car in `cars` switches its price-pill marker
   * to a circular car-photo marker (using the car's primary photo). Other cars
   * keep their price pills.
   */
  selectedCarId?: number | string | null;
  /**
   * When provided alongside `latitude`/`longitude`, draws a translucent circle
   * overlay of this radius (in meters) around the pin and fits the viewport
   * to it. Used by the host listing wizard to preview the delivery radius.
   */
  circleRadiusMeters?: number | null;
  /**
   * Optional inner circle, drawn inside the outer `circleRadiusMeters`. Used
   * to preview the free-delivery radius vs. the paid-delivery (max) radius.
   * Ignored if equal/larger than the outer radius.
   */
  freeCircleRadiusMeters?: number | null;
  onError?: (message: string) => void;
  renderError?: (message: string) => ReactNode;
  /**
   * When provided and `geoCars` is empty (no pins to fit), centres the camera
   * on this coordinate at a city-level zoom instead of staying at the London
   * default. Used by both search paths to ensure the map follows the typed
   * location even when all matching listings lack coordinates.
   */
  locationAnchor?: { lat: number; lng: number } | null;
}

export function AppleMap({
  cars,
  onBoundsChange,
  onMarkerClick,
  latitude,
  longitude,
  height = "500px",
  interactive = true,
  zoomEnabled = interactive,
  className = "",
  showSinglePin = false,
  pinImageUrl,
  selectedCarId = null,
  circleRadiusMeters = null,
  freeCircleRadiusMeters = null,
  onError,
  renderError,
  locationAnchor = null,
}: AppleMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapkit.Map | null>(null);
  const circleRef = useRef<mapkit.CircleOverlay | null>(null);
  const freeCircleRef = useRef<mapkit.CircleOverlay | null>(null);
  const [ready, setReady] = useState(false);
  // Tracks whether MapKit has actually painted tiles for the framed region.
  // Until this is true, we keep an internal shimmer overlay covering the
  // map container so the user never sees the "pins floating on a fully
  // black map" intermediate state that occurred between SearchMap mounting
  // and the first tile paint (~1–2s on a cold load).
  const [tilesReady, setTilesReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Timestamp (ms) until which region-change-end events are treated as the
  // tail of a programmatic camera move and ignored. Replaces the old fragile
  // "swallow the next N events" counter.
  const programmaticUntilRef = useRef(0);
  // Cars-only signature (sorted geo-car ids, with NO selection mixed in).
  // A selection-only change leaves this identical, so we never tear down and
  // re-fit (showItems) the camera for a hover/select.
  const carsSigRef = useRef("");
  // Live annotation objects keyed by carId + the previously-selected carId,
  // so a selection change can swap just the two affected markers.
  const annotationsByCarRef = useRef<Map<number, mapkit.Annotation>>(new Map());
  const layoutRef = useRef<Map<number, MarkerLayout>>(new Map());
  const prevSelectedRef = useRef<number | null>(null);
  // Tracks whether the map has done its first showItems() fit. The initial
  // fit must NOT animate — the map is created at a default centre (London)
  // and the first car-load would otherwise visibly pan/zoom from there to
  // the actual results, producing the "map sweep" the user reported.
  // Subsequent changes (filter updates, new bounds) keep the gentle animate.
  const initialFitDoneRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let releaseSpacing: (() => void) | null = null;
    const setMapVisible = (visible: boolean) => {
      if (visible && !releaseSpacing) {
        releaseSpacing = acquirePublicMapLauncherSpacing();
      } else if (!visible && releaseSpacing) {
        releaseSpacing();
        releaseSpacing = null;
      }
    };

    if (typeof IntersectionObserver === "undefined") {
      setMapVisible(container.offsetWidth > 0 && container.offsetHeight > 0);
      return () => setMapVisible(false);
    }

    const observer = new IntersectionObserver(
      ([entry]) => setMapVisible(Boolean(entry?.isIntersecting && entry.intersectionRatio > 0)),
      { threshold: 0.01 },
    );
    observer.observe(container);
    return () => {
      observer.disconnect();
      setMapVisible(false);
    };
  }, []);

  const geoCars = useMemo(
    () =>
      (cars || []).filter(
        (c) =>
          isValidCoord(c.latitude, c.longitude),
      ),
    [cars],
  );

  useEffect(() => {
    let destroyed = false;
    // Observers backing waitForVisibleAndSized(); tracked here so the effect
    // cleanup can disconnect them if the component unmounts before the
    // container ever becomes visible (otherwise a deferred map leaks them).
    let layoutRO: ResizeObserver | null = null;
    let layoutIO: IntersectionObserver | null = null;

    // Resolve only once the container is BOTH laid out at a non-zero size AND
    // actually intersecting the viewport. iOS Safari does not paint MapKit
    // tiles (and never fires `region-change-end`) for a map constructed while
    // its container is off-screen / below the fold — which is exactly where
    // the booking-detail "Delivery location" map lives. A below-the-fold
    // element is still laid out (non-zero offsetWidth/offsetHeight), so a
    // size-only wait constructs MapKit against an off-screen surface and the
    // map stays blank for the user (Sentry DRIVESHARE_V2-5F). Deferring
    // construction until the container is genuinely visible guarantees MapKit
    // builds against a painted surface, so tiles load and region-change-end
    // fires once the user scrolls it into view.
    function waitForVisibleAndSized(el: HTMLElement): Promise<void> {
      const hasSize = () => el.offsetWidth > 0 && el.offsetHeight > 0;

      // IntersectionObserver is unavailable under jsdom (tests) and very old
      // browsers; fall back to the original size-only wait there so behaviour
      // is unchanged when viewport visibility can't be observed.
      if (typeof IntersectionObserver === "undefined") {
        if (hasSize()) return Promise.resolve();
        return new Promise((resolve) => {
          layoutRO = new ResizeObserver((entries) => {
            for (const entry of entries) {
              const { width, height } = entry.contentRect;
              if (width > 0 && height > 0) {
                layoutRO?.disconnect();
                layoutRO = null;
                resolve();
                return;
              }
            }
          });
          layoutRO.observe(el);
        });
      }

      return new Promise((resolve) => {
        let sized = hasSize();
        let visible = false;
        const settle = () => {
          if (!sized || !visible) return;
          layoutRO?.disconnect();
          layoutRO = null;
          layoutIO?.disconnect();
          layoutIO = null;
          resolve();
        };
        if (!sized) {
          layoutRO = new ResizeObserver((entries) => {
            for (const entry of entries) {
              const { width, height } = entry.contentRect;
              if (width > 0 && height > 0) {
                sized = true;
                settle();
                return;
              }
            }
          });
          layoutRO.observe(el);
        }
        layoutIO = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting && entry.intersectionRatio > 0) {
                visible = true;
                settle();
                return;
              }
            }
          },
          { threshold: 0.01 },
        );
        layoutIO.observe(el);
      });
    }

    const container = containerRef.current;
    if (!container) return;

    // Tile-paint detection. MapKit JS doesn't expose a public "tiles
    // loaded" event, so we approximate: on the very first
    // `region-change-end` after the map is constructed (which always
    // fires once after the initial showItems), wait ~450ms for tiles to
    // actually paint, then drop the shimmer overlay. A 3.5s safety
    // timer guarantees the overlay never gets stuck if region-change-end
    // somehow never fires.
    let tileReadyTimer: number | null = null;
    let tileSafetyTimer: number | null = null;
    let blankMapReportTimer: number | null = null;
    let tileReadyHandler: (() => void) | null = null;
    let tilePaintCounter: TilePaintCounter | null = null;

    const initStartedAt = performance.now();
    mapBreadcrumb("init-effect:start");

    // Start the expensive SDK + token work as soon as a map component mounts,
    // even when iOS paint correctness requires construction to wait until the
    // container is visible. The singleton promise means route-level prewarms
    // and multiple map instances still share one request chain.
    prewarmMapKit();

    waitForVisibleAndSized(container)
      .then(() => ensureMapKit())
      .then(() => {
        if (destroyed || !containerRef.current) return;
        mapBreadcrumb("init-effect:ensure-mapkit-resolved", {
          durationMs: Math.round(performance.now() - initStartedAt),
        });

        const mk = window.mapkit;
        // Guard against non-finite pin coords (NaN/Infinity/null/empty-string).
        // A malformed `latitude`/`longitude` prop passes a bare `!= null` check
        // but produces `new mk.Coordinate(NaN, NaN)`, which MapKit rejects deep
        // in its renderer with "Expected a number for 'x' in MapRect … got NaN"
        // (Sentry DRIVESHARE_V2-9K).  isFiniteCoord also rejects empty strings
        // (Number("") = 0 via Number() but parseFloat("") = NaN).
        // Fall back to the cars' bounds or the London default when invalid.
        const hasFinitePinCoords = isFiniteCoord(latitude, longitude);
        const center = hasFinitePinCoords
          ? new mk.Coordinate(Number(latitude), Number(longitude))
          : geoCars.length > 0
            ? new mk.Coordinate(Number(geoCars[0].latitude!), Number(geoCars[0].longitude!))
            : new mk.Coordinate(51.5, -0.12);

        const isSingleLocation = showSinglePin && hasFinitePinCoords;
        // Pre-compute the bounding region from the initial cars so MapKit
        // starts fetching tiles for the correct viewport immediately.
        // Without this, the map briefly loaded tiles for `geoCars[0]` (a
        // single-point default zoom), then `showItems()` reframed and
        // MapKit had to refetch tiles for the new region — wasted bytes
        // and a visible camera "snap" the user described as "ferris".
        let initialRegion: mapkit.CoordinateRegion | undefined;
        if (isSingleLocation) {
          initialRegion = new mk.CoordinateRegion(center, new mk.CoordinateSpan(0.02, 0.02));
        } else if (geoCars.length > 0) {
          let minLat = geoCars[0].latitude!;
          let maxLat = minLat;
          let minLng = geoCars[0].longitude!;
          let maxLng = minLng;
          for (const c of geoCars) {
            const lat = c.latitude!;
            const lng = c.longitude!;
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
            if (lng < minLng) minLng = lng;
            if (lng > maxLng) maxLng = lng;
          }
          // Match the 40px padding used by the later showItems() call so
          // the framed region barely shifts when annotations are added —
          // 1.4× covers padding + a small safety margin.
          const latSpan = Math.max((maxLat - minLat) * 1.4, 0.02);
          const lngSpan = Math.max((maxLng - minLng) * 1.4, 0.02);
          initialRegion = new mk.CoordinateRegion(
            new mk.Coordinate((minLat + maxLat) / 2, (minLng + maxLng) / 2),
            new mk.CoordinateSpan(latSpan, lngSpan),
          );
        }

        const map = new mk.Map(containerRef.current, {
          center,
          region: initialRegion,
          showsCompass: mk.FeatureVisibility.Hidden,
          showsZoomControl: true,
          showsMapTypeControl: false,
          isScrollEnabled: interactive,
          isZoomEnabled: zoomEnabled,
        });

        map.cameraZoomRange = new mk.CameraZoomRange({ minCameraDistance: 200, maxCameraDistance: 20000000 });
        mapRef.current = map;
        programmaticUntilRef.current = Date.now() + PROGRAMMATIC_GUARD_MS;

        const mapConstructedAt = performance.now();
        let firstRegionChangeEnd = false;
        // Start counting tile fetches live from construction so the blank-map
        // safety timer can prove the map painted even on heavy pages where the
        // resource-timing buffer overflows (the DRIVESHARE_V2-5F false positive).
        tilePaintCounter = installTilePaintCounter(mapConstructedAt);
        mapBreadcrumb("map:constructed", {
          msSinceInitStart: Math.round(mapConstructedAt - initStartedAt),
        });

        tileReadyHandler = () => {
          firstRegionChangeEnd = true;
          mapBreadcrumb("map:first-region-change-end", {
            msSinceMapConstructed: Math.round(performance.now() - mapConstructedAt),
          });
          if (tileReadyHandler) {
            map.removeEventListener("region-change-end", tileReadyHandler);
            tileReadyHandler = null;
          }
          tileReadyTimer = window.setTimeout(() => {
            if (!destroyed) {
              mapBreadcrumb("map:tiles-ready", {
                msSinceMapConstructed: Math.round(performance.now() - mapConstructedAt),
              });
              setTilesReady(true);
            }
          }, 450);
        };
        map.addEventListener("region-change-end", tileReadyHandler);
        // Shimmer-overlay safety net: drop the placeholder after 3.5s so the
        // user is never left staring at a shimmer if region-change-end is
        // slow to fire. This is purely a UI fallback and does NOT report to
        // Sentry — slow-but-successful renders must not be flagged as blank.
        tileSafetyTimer = window.setTimeout(() => {
          if (tileReadyHandler && mapRef.current) {
            mapRef.current.removeEventListener("region-change-end", tileReadyHandler);
            tileReadyHandler = null;
          }
          if (!destroyed) setTilesReady(true);
        }, 3500);
        // Genuinely-blank-map diagnostic. A map that never reports a single
        // region-change-end is the smoking gun for the user-reported "blank
        // white map". The dominant cause turned out to be construction while
        // the container was off-screen / below the fold on iOS Safari (the
        // static "Delivery location" map on /dashboard/bookings/:id), which
        // never paints tiles and never fires region-change-end. That is now
        // fixed upstream by deferring construction until the container is
        // visible+sized (see waitForVisibleAndSized above), so a correctly-
        // rendered map fires region-change-end within ~1s of becoming visible
        // and this timer is a no-op. Other causes (Apple tile servers blocked
        // by an ad-blocker, corporate proxy, or a rejected origin token) still
        // leave the map blank and SHOULD report.
        //
        // The diagnostic therefore stays enabled for BOTH interactive and
        // non-interactive maps: a map of either kind that has not reported a
        // region-change-end ~8s after construction is verifiably still blank
        // for the user. (It was previously gated to interactive maps only, on
        // the mistaken assumption that the static booking-map's missing event
        // was a false positive — DRIVESHARE_V2-5F was a real blank map, fixed
        // by the visibility-gated construction above, not silenced here.)
        blankMapReportTimer = window.setTimeout(() => {
          // Production-only diagnostic (Sentry DRIVESHARE_V2-5F). In dev /
          // preview the Apple MapKit tile servers are routinely blocked
          // (janeway origin not on the token's allowlist, ad-blockers, slow
          // throttled tiles), so this safety timer fired as pure dev noise on
          // maps that were never going to render anyway. The blank-map signal
          // only carries meaning against the production origin, so gate the
          // report to prod; the genuine error-level mapkit captures
          // (token/script/auth failures) still report in every environment.
          // DRIVESHARE_V2-5F was largely a FALSE POSITIVE: `region-change-end`
          // is a "camera moved" signal, not "tiles painted", so a single-pin
          // static map that frames itself to its construction region never
          // fires it even though tiles painted fine. Only report when the map
          // also produced no real tile grid (see appleMapTilesPainted).
          // Prefer the buffer-overflow-immune live counter; fall back to the
          // one-shot resource-timing query when PerformanceObserver is absent.
          const painted = tilePaintCounter
            ? tilePaintCounter.painted()
            : appleMapTilesPainted(mapConstructedAt);
          if (
            !destroyed &&
            !firstRegionChangeEnd &&
            import.meta.env.PROD &&
            !painted
          ) {
            mapCaptureMessage("mapkit:safety-timer-no-region-change-end", "warning", {
              msSinceMapConstructed: Math.round(performance.now() - mapConstructedAt),
              tilesPainted: tilePaintCounter?.count() ?? null,
            });
          }
          tilePaintCounter?.disconnect();
          tilePaintCounter = null;
        }, 8000);

        setReady(true);
      })
      .catch((err) => {
        if (!destroyed) {
          const msg = err.message || "Failed to load map";
          // Task #5064 — when the init chain rejects because the external
          // Apple CDN never loaded MapKit JS (Sentry DRIVESHARE_V2-6Q), this
          // is the same pure third-party-availability noise as
          // script-load:failed (the UI degrades gracefully). Task #4690
          // downgraded it to "warning", but a warning still creates a
          // recurring Sentry event, so for that one cause record a breadcrumb
          // only.
          //
          // The init chain also rejects when the TOKEN fetch failed — which
          // fetchMapKitToken has ALREADY telemetered (capturing only real 5xx /
          // unexpected throws, breadcrumbing transient + 4xx). Re-capturing that
          // same rejection here double-counts it and is the bulk of the residual
          // DRIVESHARE_V2-6Q cascade, so for any token-originated rejection we
          // record a breadcrumb only and let the token-fetch telemetry own the
          // signal. Any OTHER internal init rejection is a genuine, actionable
          // signal and still captures at "error".
          const isTokenOriginated =
            msg.startsWith("Failed to fetch MapKit token") ||
            isTransientFetchError(err);
          if (msg === "Failed to load MapKit JS") {
            mapBreadcrumb("init-effect:rejected-cdn-unavailable", {
              error: msg,
              msSinceInitStart: Math.round(performance.now() - initStartedAt),
            });
          } else if (isTokenOriginated) {
            mapBreadcrumb("init-effect:rejected-token-originated", {
              error: msg,
              msSinceInitStart: Math.round(performance.now() - initStartedAt),
            });
          } else {
            mapCaptureMessage("mapkit:init-effect-rejected", "error", {
              error: msg,
              msSinceInitStart: Math.round(performance.now() - initStartedAt),
            });
          }
          setError(msg);
          onError?.(msg);
          // Defensive: clear the shimmer overlay even though the error UI
          // returns early before reaching the wrapper. Keeps the state
          // consistent if a caller passes `renderError` returning a tree
          // that happens to render the wrapper.
          setTilesReady(true);
        }
      });

    return () => {
      destroyed = true;
      if (layoutRO) { try { layoutRO.disconnect(); } catch { /* noop */ } layoutRO = null; }
      if (layoutIO) { try { layoutIO.disconnect(); } catch { /* noop */ } layoutIO = null; }
      if (tileReadyTimer != null) window.clearTimeout(tileReadyTimer);
      if (tileSafetyTimer != null) window.clearTimeout(tileSafetyTimer);
      if (blankMapReportTimer != null) window.clearTimeout(blankMapReportTimer);
      if (tilePaintCounter) { tilePaintCounter.disconnect(); tilePaintCounter = null; }
      if (tileReadyHandler && mapRef.current) {
        try { mapRef.current.removeEventListener("region-change-end", tileReadyHandler); } catch { /* noop */ }
      }
      if (mapRef.current) {
        mapRef.current.destroy();
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const map = mapRef.current;
    const mk = window.mapkit;

    if (showSinglePin && isFiniteCoord(latitude, longitude)) {
      // isFiniteCoord guards above ensure these are finite numbers, but TS
      // cannot narrow the prop type through the boolean helper — cast once here
      // so every mk.Coordinate call in this block gets a plain `number`.
      const lat = latitude as number;
      const lng = longitude as number;
      // This block re-frames the camera (showItems / map.region). Mark the
      // move as programmatic so the resulting region-change-end isn't treated
      // as a user pan.
      programmaticUntilRef.current = Date.now() + PROGRAMMATIC_GUARD_MS;
      map.removeAnnotations(map.annotations);
      const pin = new mk.Annotation(
        new mk.Coordinate(lat, lng),
        () => createCarPhotoMarkerElement(pinImageUrl),
        { displayPriority: 1000 },
      );
      map.addAnnotations([pin]);

      // Manage delivery-radius circle overlays. Outer = paid/max range,
      // inner (optional) = free-delivery range.
      if (circleRef.current) {
        try { map.removeOverlay(circleRef.current); } catch { /* noop */ }
        circleRef.current = null;
      }
      if (freeCircleRef.current) {
        try { map.removeOverlay(freeCircleRef.current); } catch { /* noop */ }
        freeCircleRef.current = null;
      }
      const outerRadius = circleRadiusMeters != null && Number.isFinite(circleRadiusMeters) && circleRadiusMeters > 0
        ? circleRadiusMeters
        : null;
      if (outerRadius != null) {
        const outer = new mk.CircleOverlay(
          new mk.Coordinate(lat, lng),
          outerRadius,
        );
        outer.style = new mk.Style({
          strokeColor: BRAND_GREEN,
          strokeOpacity: 0.9,
          lineWidth: 2,
          fillColor: BRAND_GREEN,
          fillOpacity: 0.12,
        });
        map.addOverlay(outer);
        circleRef.current = outer;
      }
      if (
        freeCircleRadiusMeters != null &&
        Number.isFinite(freeCircleRadiusMeters) &&
        freeCircleRadiusMeters > 0 &&
        (outerRadius == null || freeCircleRadiusMeters < outerRadius)
      ) {
        const inner = new mk.CircleOverlay(
          new mk.Coordinate(lat, lng),
          freeCircleRadiusMeters,
        );
        inner.style = new mk.Style({
          strokeColor: BRAND_GREEN,
          strokeOpacity: 1,
          lineWidth: 2,
          fillColor: BRAND_GREEN,
          fillOpacity: 0.22,
        });
        map.addOverlay(inner);
        freeCircleRef.current = inner;
      }

      // Fit viewport. When there's a delivery-radius circle, let MapKit
      // auto-fit to the circle overlay via `showItems` — that respects
      // the actual container aspect ratio so the circle's full diameter
      // is visible in BOTH axes with consistent padding.
      //
      // The earlier implementation set `map.region` to a span computed
      // purely from the circle radius in degrees. MapKit then zoomed
      // out to fit that span into a wide (e.g. 600×220) container, so
      // the visible longitudinal extent became ~3x the circle diameter
      // and the circle appeared much smaller than the radius implied.
      // Hosts on /dashboard/listings/.../resume reported a 200 km circle
      // looking like ~150 km because of this — fixed by using showItems.
      if (outerRadius != null && circleRef.current) {
        try {
          // `showItems` accepts both annotations and overlays at runtime
          // even though our narrowed mapkit.d.ts only lists annotations.
          (map as unknown as {
            showItems: (
              items: unknown[],
              opts?: { animate?: boolean; padding?: mapkit.Padding },
            ) => void;
          }).showItems([pin, circleRef.current], {
            animate: false,
            padding: new mk.Padding(24, 24, 24, 24),
          });
        } catch {
          // Fallback to the old span-based region if showItems rejects
          // overlay items in some MapKit JS build.
          const radiusDeg = (outerRadius / 111320) * 2.4;
          const lngScale = Math.max(0.2, Math.cos((lat * Math.PI) / 180));
          map.region = new mk.CoordinateRegion(
            new mk.Coordinate(lat, lng),
            new mk.CoordinateSpan(radiusDeg, radiusDeg / lngScale),
          );
        }
      } else {
        // No circle — a small default span around the single pin.
        const lngScale = Math.max(0.2, Math.cos((lat * Math.PI) / 180));
        map.region = new mk.CoordinateRegion(
          new mk.Coordinate(lat, lng),
          new mk.CoordinateSpan(0.02, 0.02 / lngScale),
        );
      }
      return;
    }

    if (geoCars.length === 0) {
      map.removeAnnotations(map.annotations);
      annotationsByCarRef.current.clear();
      layoutRef.current.clear();
      carsSigRef.current = "";
      prevSelectedRef.current = null;
      // When a geocoded location anchor is available, centre the camera on it
      // at a city-level zoom (≈2° span, ~200 km) so the map reflects the
      // typed location instead of staying at the London default. Mark as
      // programmatic so the resulting region-change-end is not mistaken for
      // a user pan and doesn't surface the "Search this area" button.
      if (locationAnchor && Number.isFinite(locationAnchor.lat) && Number.isFinite(locationAnchor.lng)) {
        programmaticUntilRef.current = Date.now() + PROGRAMMATIC_GUARD_MS;
        const cosLat = Math.max(Math.cos((locationAnchor.lat * Math.PI) / 180), 0.1);
        map.region = new mk.CoordinateRegion(
          new mk.Coordinate(locationAnchor.lat, locationAnchor.lng),
          new mk.CoordinateSpan(2, 2 / cosLat),
        );
      }
      return;
    }

    const validCars = geoCars.filter((car) => {
      const lat = Number(car.latitude);
      const lng = Number(car.longitude);
      return Number.isFinite(lat) && Number.isFinite(lng);
    });

    // Two independent change signals:
    //  - the cars set (sorted ids, NO selection mixed in) → full rebuild + camera re-fit
    //  - the selection alone → swap just two markers, camera held perfectly still
    const carsSig = validCars.map((c) => `${c.id}`).sort().join(",");
    const carsChanged = carsSig !== carsSigRef.current;
    const nextSelected =
      selectedCarId != null && selectedCarId !== "" ? Number(selectedCarId) : null;

    // (Re)build the stable per-car layout whenever the cars set changes. Each
    // car gets a fixed spiral slot at its coordinate so toggling selection
    // later never shifts any OTHER pin (the offset no longer depends on which
    // car happens to be selected).
    if (carsChanged) {
      const coordCounts = new Map<string, number>();
      for (const car of validCars) {
        const key = `${Number(car.latitude).toFixed(6)},${Number(car.longitude).toFixed(6)}`;
        coordCounts.set(key, (coordCounts.get(key) || 0) + 1);
      }
      const coordIndex = new Map<string, number>();
      const layout = new Map<number, MarkerLayout>();
      for (const car of validCars) {
        const sym = getCurrencySymbol(car.currencyCode || "USD");
        const candidates = [
          car.priceDriving,
          car.minimumPrice,
          car.chauffeurMinPrice,
          car.eventMinPrice,
          car.priceEvent,
          car.eventHourlyRate,
        ].filter((v): v is number => typeof v === "number" && Number.isFinite(v) && v > 0);
        const lowest = candidates.length > 0 ? Math.min(...candidates) : null;
        const price = lowest != null ? `${sym}${Math.round(lowest)}` : "—";
        const photoUrl = car.primaryImageCdnThumbnail || car.primaryImageCdnMedium || car.primaryImageUrl || null;

        const trueLat = Number(car.latitude);
        const trueLng = Number(car.longitude);
        const key = `${trueLat.toFixed(6)},${trueLng.toFixed(6)}`;
        const total = coordCounts.get(key) || 1;
        let offLat = trueLat;
        let offLng = trueLng;
        if (total > 1) {
          const idx = coordIndex.get(key) || 0;
          coordIndex.set(key, idx + 1);
          const angle = (2 * Math.PI * idx) / total;
          const offsetDeg = 0.00015;
          offLat += Math.cos(angle) * offsetDeg;
          offLng += Math.sin(angle) * offsetDeg;
        }

        layout.set(car.id, {
          trueLat,
          trueLng,
          offLat,
          offLng,
          price,
          photoUrl,
          carPath: buildCarPath(car),
          accessibilityLabel: `${car.year} ${car.make} ${car.model} - ${price}`,
        });
      }
      layoutRef.current = layout;
    }

    const layout = layoutRef.current;

    // Build a single annotation for a car in its given selected state. A
    // selected car sits at its true coordinate as a car-photo marker; an
    // unselected car sits at its spiral slot as its own price pill. Every
    // marker is built at Required (1000) display priority so MapKit never
    // culls overlapping pills — dense areas show one price pill per car
    // (Airbnb-style) instead of collapsing into a single aggregated marker.
    const buildAnnotation = (carId: number, isSelected: boolean): mapkit.Annotation | null => {
      const entry = layout.get(carId);
      if (!entry) return null;
      const lat = isSelected ? entry.trueLat : entry.offLat;
      const lng = isSelected ? entry.trueLng : entry.offLng;
      return new mk.Annotation(
        new mk.Coordinate(lat, lng),
        () => isSelected
          ? createSelectedCarMarkerElement(entry.photoUrl, entry.price)
          : createPricePillElement(entry.price),
        {
          displayPriority: 1000,
          data: { carId, carPath: entry.carPath },
          accessibilityLabel: entry.accessibilityLabel,
        },
      );
    };

    if (carsChanged) {
      carsSigRef.current = carsSig;
      map.removeAnnotations(map.annotations);

      const byCar = new Map<number, mapkit.Annotation>();
      const annotations: mapkit.Annotation[] = [];
      for (const car of validCars) {
        const isSelected = nextSelected != null && car.id === nextSelected;
        const ann = buildAnnotation(car.id, isSelected);
        if (ann) {
          byCar.set(car.id, ann);
          annotations.push(ann);
        }
      }
      annotationsByCarRef.current = byCar;
      prevSelectedRef.current = nextSelected;

      map.addAnnotations(annotations);
      // Mark the upcoming showItems() re-fit as programmatic so the
      // region-change-end it emits doesn't surface "Search this area".
      programmaticUntilRef.current = Date.now() + PROGRAMMATIC_GUARD_MS;
      const animateFit = initialFitDoneRef.current;
      initialFitDoneRef.current = true;
      map.showItems(annotations, {
        animate: animateFit,
        padding: new mk.Padding(40, 40, 40, 40),
      });
      return;
    }

    // Selection-only change: swap just the affected markers and DO NOT call
    // showItems() — the camera stays perfectly still and no other pin moves
    // or flickers.
    const prevSelected = prevSelectedRef.current;
    if (prevSelected === nextSelected) return;
    prevSelectedRef.current = nextSelected;

    const byCar = annotationsByCarRef.current;
    const swapMarker = (carId: number | null, isSelected: boolean) => {
      if (carId == null) return;
      const existing = byCar.get(carId);
      if (existing) {
        map.removeAnnotations([existing]);
        byCar.delete(carId);
      }
      const ann = buildAnnotation(carId, isSelected);
      if (ann) {
        map.addAnnotations([ann]);
        byCar.set(carId, ann);
      }
    };
    // Revert the previously-selected car to a price pill, and promote the
    // newly-selected car to a car-photo marker.
    swapMarker(prevSelected, false);
    swapMarker(nextSelected, true);
  }, [ready, geoCars, showSinglePin, latitude, longitude, pinImageUrl, selectedCarId, circleRadiusMeters, freeCircleRadiusMeters, locationAnchor]);

  useEffect(() => {
    if (!ready || !mapRef.current || !onMarkerClick) return;
    const map = mapRef.current;

    const handler = (event: unknown) => {
      const e = event as { annotation?: mapkit.Annotation };
      const data = e.annotation?.data as
        | { carPath?: string; carId?: number }
        | undefined;
      if (data?.carPath && onMarkerClick) onMarkerClick(data.carPath, data.carId);
    };

    map.addEventListener("select", handler);
    return () => {
      map.removeEventListener("select", handler);
    };
  }, [ready, onMarkerClick]);

  useEffect(() => {
    if (!ready || !mapRef.current || !onBoundsChange) return;
    const map = mapRef.current;

    const handler = () => {
      // Ignore bounds changes that are still within the programmatic-move
      // window (map construction or a showItems() re-fit). A genuine user
      // pan/zoom always lands after this window and surfaces "Search this
      // area"; selection changes never call showItems() at all (see above).
      if (Date.now() < programmaticUntilRef.current) return;
      const bounds = map.region.toBoundingRegion();
      // Guard: a pinch-to-zoom gesture on a region that already contains NaN
      // coordinates (from a listing with null/NaN DB coords) propagates NaN
      // through Math.max/min and crashes MapKit's internal geometry constructors
      // (DRIVESHARE_V2-EG, DRIVESHARE_V2-EF). Bail out before clamping so NaN
      // never reaches onBoundsChange or MapKit's rect constructor.
      if (
        !Number.isFinite(bounds.northLatitude) ||
        !Number.isFinite(bounds.southLatitude) ||
        !Number.isFinite(bounds.eastLongitude) ||
        !Number.isFinite(bounds.westLongitude)
      ) {
        console.warn("[AppleMap] region-change-end produced non-finite bounds — skipping", {
          north: bounds.northLatitude,
          south: bounds.southLatitude,
          east: bounds.eastLongitude,
          west: bounds.westLongitude,
        });
        return;
      }
      const clampLat = (v: number) => Math.max(-90, Math.min(90, v));
      const clampLng = (v: number) => Math.max(-180, Math.min(180, v));
      onBoundsChange({
        north: clampLat(bounds.northLatitude),
        south: clampLat(bounds.southLatitude),
        east: clampLng(bounds.eastLongitude),
        west: clampLng(bounds.westLongitude),
      });
    };

    map.addEventListener("region-change-end", handler);
    return () => {
      map.removeEventListener("region-change-end", handler);
    };
  }, [ready, onBoundsChange]);

  if (error) {
    if (renderError) {
      return <>{renderError(error)}</>;
    }
    return (
      <div className={`flex items-center justify-center rounded-2xl border border-border bg-muted/30 ${className}`} style={{ height }}>
        <div className="text-center px-4">
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  // The MapKit container is wrapped in a positioned parent so the
  // shimmer overlay can sit on top of the map's own DOM (which MapKit
  // injects into containerRef). Without this overlay, users saw the
  // outer column shimmer disappear → empty/black map container → pins
  // floating on still-loading tiles, then finally tiles + pins together.
  // The overlay collapses that to a single continuous shimmer until
  // the first tile paint completes.
  return (
    <div
      className={`relative rounded-2xl overflow-hidden ${className}`}
      style={{ height, width: "100%" }}
    >
      <div ref={containerRef} className="absolute inset-0" />
      {!tilesReady && (
        <div
          className="bac-shimmer pointer-events-none absolute inset-0 z-[5]"
          aria-hidden="true"
        />
      )}
    </div>
  );
}
